/**
 * Competencies Management Page
 *
 * HR and Admin page for managing competencies.
 * Uses the new many-to-many relationship between competencies and tasks.
 */

import { useState, useMemo } from 'react';
import { Layout } from '../../components/Layout';
import {
  useCompetencies,
  useCreateCompetency,
  useUpdateCompetency,
  useDeleteCompetency,
} from './useCompetencies';
import { useTasks } from '../tasks/useTasks';
import type { Competency, CreateCompetencyInput, UpdateCompetencyInput } from '../../lib/competenciesApi';

export function CompetenciesPage() {
  const { data: competencies, isLoading, error } = useCompetencies();
  const { data: tasks } = useTasks();
  const createCompetency = useCreateCompetency();
  const updateCompetency = useUpdateCompetency();
  const deleteCompetency = useDeleteCompetency();

  const [filterType, setFilterType] = useState<'all' | 'basis' | 'vak'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCompetency, setEditingCompetency] = useState<Competency | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Competency | null>(null);

  // Form state - uses taskIds array for many-to-many
  const [formData, setFormData] = useState<CreateCompetencyInput>({
    name: '',
    beschrijving: '',
    type: 'basis',
    taskIds: [],
  });

  // Filter competencies by type
  const filteredCompetencies = useMemo(() => {
    if (!competencies) return [];
    if (filterType === 'all') return competencies;
    return competencies.filter(c => c.type === filterType);
  }, [competencies, filterType]);

  // Group by type for display
  const basisCompetencies = useMemo(() =>
    competencies?.filter(c => c.type === 'basis') ?? [], [competencies]);
  const vakCompetencies = useMemo(() =>
    competencies?.filter(c => c.type === 'vak') ?? [], [competencies]);

  function resetForm() {
    setFormData({
      name: '',
      beschrijving: '',
      type: 'basis',
      taskIds: [],
    });
  }

  function openCreateModal() {
    resetForm();
    setEditingCompetency(null);
    setShowCreateModal(true);
  }

  function openEditModal(competency: Competency) {
    setFormData({
      name: competency.name,
      beschrijving: competency.beschrijving,
      type: competency.type,
      taskIds: competency.linkedTasks?.map(t => t.id) ?? [],
    });
    setEditingCompetency(competency);
    setShowCreateModal(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    try {
      if (editingCompetency) {
        const input: UpdateCompetencyInput = {
          name: formData.name,
          beschrijving: formData.beschrijving || null,
          type: formData.type,
          taskIds: formData.type === 'vak' ? formData.taskIds : [],
        };
        await updateCompetency.mutateAsync({ id: editingCompetency.id, input });
      } else {
        await createCompetency.mutateAsync({
          ...formData,
          beschrijving: formData.beschrijving || null,
          taskIds: formData.type === 'vak' ? formData.taskIds : [],
        });
      }
      setShowCreateModal(false);
      resetForm();
    } catch {
      // Error handled by mutation state
    }
  }

  async function handleDelete() {
    if (!deleteConfirm) return;

    try {
      await deleteCompetency.mutateAsync(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch {
      // Error handled by mutation state
    }
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Competenties laden...</span>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700">Fout bij het laden van competenties.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="competencies-page">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Competenties</h1>
            <p className="mt-1 text-sm text-gray-500">
              Beheer basis- en vakcompetenties
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nieuwe Competentie
          </button>
        </div>

        {/* Stats */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-sm font-medium text-gray-500">Totaal</div>
            <div className="text-2xl font-bold text-gray-900">{competencies?.length ?? 0}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-sm font-medium text-gray-500">Basiscompetenties</div>
            <div className="text-2xl font-bold text-blue-600">{basisCompetencies.length}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-sm font-medium text-gray-500">Vakcompetenties</div>
            <div className="text-2xl font-bold text-green-600">{vakCompetencies.length}</div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mb-4 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {(['all', 'basis', 'vak'] as const).map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  filterType === type
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {type === 'all' ? 'Alle' : type === 'basis' ? 'Basis' : 'Vak'}
                <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                  {type === 'all'
                    ? competencies?.length ?? 0
                    : type === 'basis'
                      ? basisCompetencies.length
                      : vakCompetencies.length}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Competencies List */}
        {filteredCompetencies.length === 0 ? (
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p className="mt-4 text-gray-500">Geen competenties gevonden.</p>
            <button
              onClick={openCreateModal}
              className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
            >
              Voeg je eerste competentie toe
            </button>
          </div>
        ) : (
          <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Naam
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gekoppelde Taken
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Beschrijving
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acties
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCompetencies.map((competency) => (
                  <tr key={competency.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{competency.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        competency.type === 'basis'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {competency.type === 'basis' ? 'Basis' : 'Vak'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {competency.linkedTasks && competency.linkedTasks.length > 0 ? (
                        <span className="text-sm text-gray-600">
                          {competency.linkedTasks.map(t => t.name).join(', ')}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-500 truncate max-w-xs">
                        {competency.beschrijving || '-'}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => openEditModal(competency)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Bewerken
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(competency)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Verwijderen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <form onSubmit={handleSubmit}>
                  <div>
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {editingCompetency ? 'Competentie Bewerken' : 'Nieuwe Competentie'}
                    </h3>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Naam *
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Type *
                        </label>
                        <select
                          value={formData.type}
                          onChange={(e) => setFormData({
                            ...formData,
                            type: e.target.value as 'basis' | 'vak',
                            taskIds: e.target.value === 'basis' ? [] : formData.taskIds,
                          })}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        >
                          <option value="basis">Basis (algemeen)</option>
                          <option value="vak">Vak (taakspecifiek)</option>
                        </select>
                      </div>

                      {formData.type === 'vak' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Gekoppelde Taken
                          </label>
                          <select
                            multiple
                            value={formData.taskIds?.map(String) ?? []}
                            onChange={(e) => setFormData({
                              ...formData,
                              taskIds: Array.from(e.target.selectedOptions, opt => Number(opt.value)),
                            })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            size={5}
                          >
                            {tasks?.data?.map((task) => (
                              <option key={task.id} value={task.id}>
                                {task.name}
                              </option>
                            ))}
                          </select>
                          <p className="mt-1 text-xs text-gray-500">
                            Houd Ctrl/Cmd ingedrukt om meerdere taken te selecteren
                          </p>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Beschrijving
                        </label>
                        <textarea
                          value={formData.beschrijving ?? ''}
                          onChange={(e) => setFormData({ ...formData, beschrijving: e.target.value })}
                          rows={3}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {(createCompetency.error || updateCompetency.error) && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-700">
                        Er is een fout opgetreden. Probeer het opnieuw.
                      </p>
                    </div>
                  )}

                  <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      disabled={createCompetency.isPending || updateCompetency.isPending}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      {(createCompetency.isPending || updateCompetency.isPending) ? 'Opslaan...' : 'Opslaan'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateModal(false);
                        resetForm();
                      }}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                    >
                      Annuleren
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Competentie verwijderen
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Weet u zeker dat u <strong>{deleteConfirm.name}</strong> wilt verwijderen?
                        Deze actie kan niet ongedaan worden gemaakt.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleteCompetency.isPending}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {deleteCompetency.isPending ? 'Verwijderen...' : 'Verwijderen'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(null)}
                    disabled={deleteCompetency.isPending}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Annuleren
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
